import { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import type { WorkOrderMaterialIssueBoardResponse } from '../../services/types';
import { Button } from '../../components/ui/button';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';

type BoardRow = WorkOrderMaterialIssueBoardResponse['rows'][number];

const formatQty = (value: number | string | null | undefined) => {
    const numeric = Number(value ?? 0);
    if (!Number.isFinite(numeric)) return '0';
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(numeric);
};

const rowKey = (row: BoardRow) => `${row.wo_id}:${String(row.row_id)}:${row.item_id}`;

export function MaterialIssueBoard() {
    const [board, setBoard] = useState<WorkOrderMaterialIssueBoardResponse>({ work_orders: [], rows: [] });
    const [loading, setLoading] = useState(true);
    const [posting, setPosting] = useState(false);
    const [query, setQuery] = useState('');
    const [sourceFilter, setSourceFilter] = useState<'ALL' | 'ESTIMATE' | 'RECIPE' | 'MANUAL'>('ALL');
    const [selected, setSelected] = useState<Record<string, boolean>>({});
    const [qtyByKey, setQtyByKey] = useState<Record<string, string>>({});

    const loadBoard = async () => {
        try {
            setLoading(true);
            const result = await api.manufacturing.getMaterialIssueBoard();
            setBoard(result);
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load material issue board');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void loadBoard();
    }, []);

    const issueRows = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (board.rows || [])
            .filter((row) => Number(row.item_id || 0) > 0)
            .filter((row) => Number(row.remaining_to_issue_qty ?? row.remaining_qty ?? 0) > 0)
            .filter((row) => {
                const source = String(row.source_type || (row.row_type === 'UNPLANNED' ? 'MANUAL' : 'ESTIMATE')).toUpperCase();
                return sourceFilter === 'ALL' || source === sourceFilter || (sourceFilter === 'MANUAL' && row.row_type === 'UNPLANNED');
            })
            .filter((row) => {
                if (!q) return true;
                return `${row.wo_number} ${row.item_code} ${row.item_name} ${row.source_type || ''} ${row.line_label || ''}`.toLowerCase().includes(q);
            });
    }, [board.rows, query, sourceFilter]);

    const selectedCount = Object.values(selected).filter(Boolean).length;

    const postSelected = async () => {
        const lines = issueRows
            .filter((row) => selected[rowKey(row)])
            .map((row) => {
                const key = rowKey(row);
                const qty = Number(qtyByKey[key] || row.remaining_to_issue_qty || row.remaining_qty || 0);
                return {
                    wo_id: Number(row.wo_id),
                    item_id: Number(row.item_id),
                    qty,
                    transaction_unit_id: row.base_unit_id || undefined,
                    row_type: row.row_type,
                    source_type: row.source_type || (row.row_type === 'UNPLANNED' ? 'MANUAL' : 'ESTIMATE'),
                    source_ref: row.source_ref || undefined,
                    wo_item_id: row.wo_item_id || undefined,
                    material_row_id: row.row_type === 'UNPLANNED' && typeof row.row_id === 'number' ? Number(row.row_id) : undefined,
                };
            })
            .filter((line) => line.qty > 0);
        if (!lines.length) {
            toast.error('Select at least one material row with quantity');
            return;
        }
        try {
            setPosting(true);
            const result = await api.manufacturing.postMaterialIssues({ lines });
            toast.success(`Issued ${result.posted_count} material line(s) to WO balance`);
            setSelected({});
            setQtyByKey({});
            await loadBoard();
        } catch (err: any) {
            toast.error(err?.message || err?.detail?.message || 'Failed to issue selected materials');
        } finally {
            setPosting(false);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-2xl font-bold text-slate-900">Material Issue</h2>
                <p className="text-sm text-slate-500">Issue material from Store to WO balance. Daily Entry will consume from this balance.</p>
            </div>

            <div className="rounded-lg border bg-white p-3 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <TableFilterBar
                        searchValue={query}
                        onSearchChange={setQuery}
                        searchPlaceholder="Search WO, material, source, or part..."
                        isDirty={!!query}
                        onClear={() => setQuery('')}
                        rightSlot={`${issueRows.length} rows`}
                    />
                    <Button type="button" variant="outline" onClick={() => void loadBoard()} disabled={loading}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Refresh
                    </Button>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                    {(['ALL', 'ESTIMATE', 'RECIPE', 'MANUAL'] as const).map((source) => (
                        <button
                            key={source}
                            type="button"
                            onClick={() => setSourceFilter(source)}
                            className={`rounded-full border px-3 py-1 font-medium ${sourceFilter === source ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                        >
                            {source === 'ALL' ? 'All Sources' : source === 'ESTIMATE' ? 'Estimate' : source === 'RECIPE' ? 'Recipe' : 'Manual'}
                        </button>
                    ))}
                </div>
            </div>

            <div className="overflow-x-auto rounded-lg border bg-white">
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                        <tr>
                            <th className="px-3 py-2 text-left">Select</th>
                            <th className="px-3 py-2 text-left">WO / Line</th>
                            <th className="px-3 py-2 text-left">Material</th>
                            <th className="px-3 py-2 text-left">Source</th>
                            <th className="px-3 py-2 text-right">Required</th>
                            <th className="px-3 py-2 text-right">Issued</th>
                            <th className="px-3 py-2 text-right">Consumed</th>
                            <th className="px-3 py-2 text-right">To Issue</th>
                            <th className="px-3 py-2 text-right">Available</th>
                            <th className="px-3 py-2 text-right">Issue Qty</th>
                        </tr>
                    </thead>
                    <tbody>
                        {issueRows.map((row) => {
                            const key = rowKey(row);
                            const toIssue = Number(row.remaining_to_issue_qty ?? row.remaining_qty ?? 0);
                            const shortage = Number(row.shortage_qty || 0);
                            return (
                                <tr key={key} className="border-t">
                                    <td className="px-3 py-2">
                                        <input
                                            type="checkbox"
                                            checked={!!selected[key]}
                                            onChange={(event) => setSelected((prev) => ({ ...prev, [key]: event.target.checked }))}
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-slate-900">{row.wo_number}</div>
                                        <div className="text-xs text-slate-500">{row.line_label || 'WO level'}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-slate-900">{row.item_code} - {row.item_name}</div>
                                        <div className="text-xs text-slate-500">{row.base_unit_name || ''}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                                            {row.source_type || (row.row_type === 'UNPLANNED' ? 'MANUAL' : 'ESTIMATE')}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right">{formatQty(row.required_qty)}</td>
                                    <td className="px-3 py-2 text-right">{formatQty(row.issued_to_wo_qty ?? row.issued_qty)}</td>
                                    <td className="px-3 py-2 text-right">{formatQty(row.consumed_qty || 0)}</td>
                                    <td className="px-3 py-2 text-right font-semibold">{formatQty(toIssue)}</td>
                                    <td className={shortage > 0 ? 'px-3 py-2 text-right font-semibold text-red-600' : 'px-3 py-2 text-right'}>
                                        {formatQty(row.available_qty)}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.001"
                                            className="w-24 rounded border px-2 py-1 text-right"
                                            value={qtyByKey[key] ?? String(toIssue)}
                                            onChange={(event) => setQtyByKey((prev) => ({ ...prev, [key]: event.target.value }))}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                        {!issueRows.length ? (
                            <tr>
                                <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                                    {loading ? 'Loading material issue board...' : 'No pending material issue rows found.'}
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>

            <div className="flex justify-end">
                <Button type="button" onClick={postSelected} disabled={posting || selectedCount === 0}>
                    Issue Selected to WO
                </Button>
            </div>
        </div>
    );
}
